import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, LogOut, Mail, RefreshCw, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function formatDate(value) {
  try {
    const date = new Date(value);
    return date.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return value;
  }
}

export default function AdminInbox() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | handled
  const [query, setQuery] = useState("");
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login", { replace: true });
  }, [authLoading, user, navigate]);

  const load = async () => {
    setError("");
    try {
      const response = await fetch(`${BACKEND}/api/contact`, { credentials: "include" });
      if (response.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      if (!response.ok) throw new Error("Could not load enquiries");
      const data = await response.json();
      setItems(data);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter((item) => {
      if (filter === "pending" && item.handled) return false;
      if (filter === "handled" && !item.handled) return false;
      if (!query) return true;
      const haystack = `${item.name} ${item.email} ${item.subject} ${item.message}`.toLowerCase();
      return haystack.includes(query.toLowerCase());
    });
  }, [items, filter, query]);

  const toggleHandled = async (enquiry) => {
    setUpdating(enquiry.id);
    try {
      const response = await fetch(`${BACKEND}/api/contact/${enquiry.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ handled: !enquiry.handled }),
      });
      if (!response.ok) throw new Error("Could not update");
      const updated = await response.json();
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    } catch (err) {
      setError(err.message || "Update failed");
    } finally {
      setUpdating(null);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (authLoading || !user) {
    return (
      <main className="admin-shell" data-testid="admin-loader">
        <div className="admin-loader"><Loader2 className="spin" size={22} /><p>Loading…</p></div>
      </main>
    );
  }

  const pendingCount = items ? items.filter((i) => !i.handled).length : 0;

  return (
    <main className="admin-page" data-testid="admin-inbox">
      <header className="admin-header">
        <div>
          <Link to="/" className="admin-brand" data-testid="admin-brand">
            <span className="brand-mark">N</span>
            <div>
              <small>Nawan Laboratories</small>
              <b>Enquiry Inbox</b>
            </div>
          </Link>
        </div>
        <div className="admin-header-right">
          <span className="admin-user" data-testid="admin-user">
            {user.picture ? <img src={user.picture} alt={user.name} /> : <span className="admin-user-glyph">{user.name?.[0] || "A"}</span>}
            <span>
              <b>{user.name}</b>
              <small>{user.email}</small>
            </span>
          </span>
          <button className="admin-icon-btn" onClick={doLogout} data-testid="admin-logout-btn">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <section className="admin-toolbar">
        <div className="admin-stats" data-testid="admin-stats">
          <span><b>{items?.length ?? "—"}</b> total</span>
          <span className={pendingCount ? "warn" : ""}><b>{pendingCount}</b> pending</span>
        </div>
        <div className="admin-controls">
          <label className="admin-search">
            <Search size={15} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, subject or message"
              data-testid="admin-search-input"
            />
          </label>
          <div className="admin-filter-tabs" data-testid="admin-filter-tabs">
            {[
              ["all", "All"],
              ["pending", "Pending"],
              ["handled", "Handled"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={filter === key ? "selected" : ""}
                data-testid={`admin-filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
          <button className="admin-icon-btn" onClick={load} data-testid="admin-refresh">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </section>

      {error && <p className="admin-error" data-testid="admin-error">{error}</p>}

      {!items ? (
        <div className="admin-loader"><Loader2 className="spin" size={22} /><p>Fetching enquiries…</p></div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty" data-testid="admin-empty">
          <p>Nothing here yet. Enquiries from the public contact form will appear on this page.</p>
        </div>
      ) : (
        <ul className="admin-list" data-testid="admin-list">
          {filtered.map((enquiry) => (
            <li
              key={enquiry.id}
              className={`admin-card ${enquiry.handled ? "is-handled" : "is-pending"}`}
              data-testid={`admin-enquiry-${enquiry.id}`}
            >
              <div className="admin-card-head">
                <span className={`admin-badge ${enquiry.handled ? "handled" : "pending"}`}>
                  {enquiry.handled ? "Handled" : "Pending"}
                </span>
                <time>{formatDate(enquiry.created_at)}</time>
              </div>
              <h3>{enquiry.subject}</h3>
              <p className="admin-card-meta">
                <b>{enquiry.name}</b>
                <a href={`mailto:${enquiry.email}?subject=Re: ${encodeURIComponent(enquiry.subject)}`}>
                  <Mail size={13} /> {enquiry.email}
                </a>
                {enquiry.phone && <span>· {enquiry.phone}</span>}
              </p>
              <p className="admin-card-body">{enquiry.message}</p>
              <div className="admin-card-actions">
                <button
                  onClick={() => toggleHandled(enquiry)}
                  disabled={updating === enquiry.id}
                  className={enquiry.handled ? "outline" : "primary"}
                  data-testid={`admin-toggle-${enquiry.id}`}
                >
                  {updating === enquiry.id ? (
                    <><Loader2 size={14} className="spin" /> Saving</>
                  ) : enquiry.handled ? (
                    "Mark as pending"
                  ) : (
                    <><Check size={14} /> Mark as handled</>
                  )}
                </button>
                <a
                  href={`mailto:${enquiry.email}?subject=Re: ${encodeURIComponent(enquiry.subject)}&body=${encodeURIComponent("\n\n--- Original ---\n" + enquiry.message)}`}
                  className="outline"
                >
                  Reply
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
